import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { countryNames } from '@/data/countryCities';
import { CountryCode } from '@/types';
import { useToast } from '@/hooks/use-toast';

interface ScrapedLead {
  id: string;
  name: string;
  website: string | null;
  phones: string[];
  emails: string[];
  instagram: string | null;
  facebook: string | null;
  address: string | null;
  google_maps_url: string | null;
  source_url: string;
  description: string;
  rating: number | null;
  reviews_count: number;
  category_name: string | null;
}

interface ScraperProgress {
  current: number;
  total: number;
  currentCity: string;
  currentCategory: string;
}

interface ScraperState {
  loading: boolean;
  progress: ScraperProgress;
  results: ScrapedLead[];
  selected: Set<string>;
  importing: boolean;
}

interface ScraperContextValue extends ScraperState {
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  setImporting: React.Dispatch<React.SetStateAction<boolean>>;
  setResults: React.Dispatch<React.SetStateAction<ScrapedLead[]>>;
  startSearch: (params: {
    selectedCountry: CountryCode;
    searchMode: 'country' | 'cities';
    selectedCities: string[];
    categories: string[];
    limitPerCategory: number;
  }) => Promise<void>;
  abortSearch: () => void;
}

const ScraperContext = createContext<ScraperContextValue | null>(null);

export const useScraperContext = () => {
  const ctx = useContext(ScraperContext);
  if (!ctx) throw new Error('useScraperContext must be used within ScraperProvider');
  return ctx;
};

export const ScraperProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<ScraperProgress>({ current: 0, total: 0, currentCity: '', currentCategory: '' });
  const [results, setResults] = useState<ScrapedLead[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const abortRef = useRef(false);

  const abortSearch = useCallback(() => {
    abortRef.current = true;
  }, []);

  const startSearch = useCallback(async (params: {
    selectedCountry: CountryCode;
    searchMode: 'country' | 'cities';
    selectedCities: string[];
    categories: string[];
    limitPerCategory: number;
  }) => {
    const { selectedCountry, searchMode, selectedCities, categories, limitPerCategory } = params;
    
    abortRef.current = false;
    setLoading(true);
    setResults([]);
    setSelected(new Set());

    const allResults: ScrapedLead[] = [];
    const countryName = countryNames[selectedCountry] || selectedCountry;

    const jobs: { city: string; category: string }[] = [];
    if (searchMode === 'country') {
      categories.forEach(cat => jobs.push({ city: '', category: cat }));
    } else {
      selectedCities.forEach(city => {
        categories.forEach(cat => jobs.push({ city, category: cat }));
      });
    }

    const total = jobs.length;

    for (let i = 0; i < jobs.length; i++) {
      if (abortRef.current) break;
      
      const job = jobs[i];
      setProgress({ current: i + 1, total, currentCity: job.city || countryName, currentCategory: job.category });

      try {
        const { data, error } = await supabase.functions.invoke('scraper-search', {
          body: {
            category: job.category,
            city: job.city || undefined,
            country: countryName,
            limit: limitPerCategory,
          },
        });

        if (error) {
          console.error(`Error for ${job.city}/${job.category}:`, error);
          continue;
        }
        if (!data?.success) {
          console.error(`Failed for ${job.city}/${job.category}:`, data?.error);
          continue;
        }

        const leads: ScrapedLead[] = (data.data || []).map((item: any, idx: number) => ({
          ...item,
          id: `lead-${Date.now()}-${i}-${idx}`,
        }));

        allResults.push(...leads);
        setResults([...allResults]);
      } catch (err: any) {
        console.error(`Search error for ${job.city}/${job.category}:`, err);
      }
    }

    setLoading(false);

    // Deduplicate by name + phone
    const seen = new Set<string>();
    const deduped = allResults.filter(lead => {
      const key = `${lead.name.toLowerCase()}-${lead.phones[0] || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    setResults(deduped);
    setProgress(prev => ({ ...prev })); // keep last progress visible
    toast({ title: 'Search Complete', description: `Found ${deduped.length} unique businesses across ${total} searches` });
  }, [toast]);

  return (
    <ScraperContext.Provider value={{
      loading,
      progress,
      results,
      selected,
      importing,
      setSelected,
      setImporting,
      setResults,
      startSearch,
      abortSearch,
    }}>
      {children}
    </ScraperContext.Provider>
  );
};
